import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '@/api';

// Types
export type ToolType = 'select' | 'text' | 'sign' | 'draw' | 'image' | 'erase' | null;
export type FieldType = 'signature' | 'initial' | 'text' | 'date' | 'email' | 'checkbox';
export type ObjectType = 'text' | 'image' | 'drawing' | 'signature' | 'field' | 'pageNumber';

export interface EditorObject {
  id: string;
  type: ObjectType;
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  zIndex: number;
  locked: boolean;
  data: Record<string, unknown>;
  fabricJson?: object;
}

export interface PageState {
  pageNumber: number;
  originalWidth: number;
  originalHeight: number;
  rotation: number;
  thumbnailDataUrl: string;
  objects: EditorObject[];
}

export interface EditDelta {
  id: string;
  type: 'add' | 'modify' | 'delete';
  objectId: string;
  objectType: ObjectType;
  pageNumber: number;
  timestamp: number;
  previousState?: EditorObject;
  newState?: EditorObject;
}

export interface ToolSettings {
  // Text settings
  fontFamily: string;
  fontSize: number;
  fontColor: string;
  fontBold: boolean;
  fontItalic: boolean;
  // Draw settings
  strokeColor: string;
  strokeWidth: number;
  strokeOpacity: number;
  // Field settings
  fieldType: FieldType;
  fieldRequired: boolean;
  fieldReadOnly: boolean;
}

export interface DocumentInfo {
  id: string;
  name: string;
  documentKey: string;
  pageCount: number;
  status: string;
}

export const usePdfEditorStore = defineStore('pdfEditor', () => {
  // Document state
  const documentId = ref<string | null>(null);
  const documentInfo = ref<DocumentInfo | null>(null);
  const pdfBytes = ref<ArrayBuffer | null>(null);
  const loading = ref(false);
  const saving = ref(false);
  const error = ref<string | null>(null);

  // Pages state
  const pages = ref<PageState[]>([]);
  const currentPageIndex = ref(0);
  const totalPages = computed(() => pages.value.length);

  // Viewing state
  const zoom = ref(100);
  const viewMode = ref<'single' | 'continuous'>('continuous');

  // Tool state
  const activeTool = ref<ToolType>('select');
  const toolSettings = ref<ToolSettings>({
    fontFamily: 'Helvetica, Arial, sans-serif',
    fontSize: 16,
    fontColor: '#000000',
    fontBold: false,
    fontItalic: false,
    strokeColor: '#000000',
    strokeWidth: 2,
    strokeOpacity: 1,
    fieldType: 'text',
    fieldRequired: false,
    fieldReadOnly: false,
  });

  // Selection state
  const selectedObjectIds = ref<string[]>([]);
  const clipboard = ref<EditorObject[]>([]);

  // Edit history (undo/redo)
  const editHistory = ref<EditDelta[]>([]);
  const historyIndex = ref(-1);
  const pendingEdits = ref<EditDelta[]>([]); // Edits not yet saved to backend

  // Auto-save state
  const autoSaveEnabled = ref(true);
  const lastSaveTime = ref<Date | null>(null);
  const isDirty = computed(() => pendingEdits.value.length > 0);

  // Computed
  const currentPage = computed(() => pages.value[currentPageIndex.value] || null);
  const canUndo = computed(() => historyIndex.value >= 0);
  const canRedo = computed(() => historyIndex.value < editHistory.value.length - 1);

  const selectedObjects = computed(() => {
    const allObjects = pages.value.flatMap(p => p.objects);
    return allObjects.filter(obj => selectedObjectIds.value.includes(obj.id));
  });

  // Actions
  const loadDocument = async (id: string) => {
    loading.value = true;
    error.value = null;
    documentId.value = id;

    try {
      // Fetch document info
      const response = await api.get(`/documents/${id}`);
      const doc = response.data.data;

      documentInfo.value = {
        id: doc.id,
        name: doc.name || doc.document_name,
        documentKey: doc.document_key,
        pageCount: doc.page_count || 1,
        status: doc.status,
      };

      // Load existing edits if any
      try {
        const editsResponse = await api.get(`/documents/${id}/editor/edits`);
        if (editsResponse.data.data?.edits) {
          editHistory.value = editsResponse.data.data.edits;
          historyIndex.value = editHistory.value.length - 1;
        }
      } catch {
        // No saved edits, that's fine
      }

      return documentInfo.value;
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message || 'Failed to load document';
      error.value = message;
      throw new Error(message);
    } finally {
      loading.value = false;
    }
  };

  const setPages = (pageData: PageState[]) => {
    pages.value = pageData;
  };

  const goToPage = (pageIndex: number) => {
    if (pageIndex >= 0 && pageIndex < totalPages.value) {
      currentPageIndex.value = pageIndex;
    }
  };

  const setZoom = (newZoom: number) => {
    zoom.value = Math.min(300, Math.max(25, newZoom));
  };

  const zoomIn = () => setZoom(zoom.value + 25);
  const zoomOut = () => setZoom(zoom.value - 25);

  const setActiveTool = (tool: ToolType) => {
    activeTool.value = tool;
    if (tool !== 'select') {
      clearSelection();
    }
  };

  const updateToolSettings = (settings: Partial<ToolSettings>) => {
    toolSettings.value = { ...toolSettings.value, ...settings };
  };

  // Object management
  const addObject = (object: EditorObject) => {
    const page = pages.value.find(p => p.pageNumber === object.pageNumber);
    if (!page) return;

    page.objects.push(object);

    // Create edit delta
    const delta: EditDelta = {
      id: `edit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'add',
      objectId: object.id,
      objectType: object.type,
      pageNumber: object.pageNumber,
      timestamp: Date.now(),
      newState: { ...object },
    };

    pushToHistory(delta);
  };

  const updateObject = (objectId: string, updates: Partial<EditorObject>) => {
    for (const page of pages.value) {
      const objIndex = page.objects.findIndex(o => o.id === objectId);
      if (objIndex !== -1) {
        const previousState = { ...page.objects[objIndex] };
        page.objects[objIndex] = { ...page.objects[objIndex], ...updates };

        const delta: EditDelta = {
          id: `edit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'modify',
          objectId,
          objectType: page.objects[objIndex].type,
          pageNumber: page.pageNumber,
          timestamp: Date.now(),
          previousState,
          newState: { ...page.objects[objIndex] },
        };

        pushToHistory(delta);
        return;
      }
    }
  };

  const deleteObject = (objectId: string) => {
    for (const page of pages.value) {
      const objIndex = page.objects.findIndex(o => o.id === objectId);
      if (objIndex !== -1) {
        const previousState = { ...page.objects[objIndex] };
        page.objects.splice(objIndex, 1);

        const delta: EditDelta = {
          id: `edit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'delete',
          objectId,
          objectType: previousState.type,
          pageNumber: page.pageNumber,
          timestamp: Date.now(),
          previousState,
        };

        pushToHistory(delta);
        return;
      }
    }
  };

  const deleteSelectedObjects = () => {
    for (const id of [...selectedObjectIds.value]) {
      deleteObject(id);
    }
    clearSelection();
  };

  // Selection management
  const selectObject = (objectId: string, addToSelection = false) => {
    if (addToSelection) {
      if (!selectedObjectIds.value.includes(objectId)) {
        selectedObjectIds.value.push(objectId);
      }
    } else {
      selectedObjectIds.value = [objectId];
    }
  };

  const clearSelection = () => {
    selectedObjectIds.value = [];
  };

  const selectAllOnPage = () => {
    const page = currentPage.value;
    if (page) {
      selectedObjectIds.value = page.objects.map(o => o.id);
    }
  };

  // Clipboard operations
  const copyToClipboard = () => {
    clipboard.value = selectedObjects.value.map(obj => ({ ...obj }));
  };

  const pasteFromClipboard = () => {
    if (clipboard.value.length === 0) return;

    const page = currentPage.value;
    if (!page) return;

    const newIds: string[] = [];
    for (const obj of clipboard.value) {
      const newObj: EditorObject = {
        ...obj,
        id: `obj-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        pageNumber: page.pageNumber,
        x: obj.x + 20,
        y: obj.y + 20,
      };
      addObject(newObj);
      newIds.push(newObj.id);
    }

    selectedObjectIds.value = newIds;
  };

  // History management
  const pushToHistory = (delta: EditDelta) => {
    // Remove any redo states
    if (historyIndex.value < editHistory.value.length - 1) {
      editHistory.value = editHistory.value.slice(0, historyIndex.value + 1);
    }

    editHistory.value.push(delta);
    historyIndex.value++;
    pendingEdits.value.push(delta);

    // Limit history size
    const maxHistory = 100;
    if (editHistory.value.length > maxHistory) {
      editHistory.value.shift();
      historyIndex.value--;
    }
  };

  const undo = () => {
    if (!canUndo.value) return;

    const delta = editHistory.value[historyIndex.value];
    applyDeltaReverse(delta);
    historyIndex.value--;
  };

  const redo = () => {
    if (!canRedo.value) return;

    historyIndex.value++;
    const delta = editHistory.value[historyIndex.value];
    applyDelta(delta);
  };

  const applyDelta = (delta: EditDelta) => {
    const page = pages.value.find(p => p.pageNumber === delta.pageNumber);
    if (!page) return;

    switch (delta.type) {
      case 'add':
        if (delta.newState) {
          page.objects.push(delta.newState);
        }
        break;
      case 'modify':
        if (delta.newState) {
          const objIndex = page.objects.findIndex(o => o.id === delta.objectId);
          if (objIndex !== -1) {
            page.objects[objIndex] = delta.newState;
          }
        }
        break;
      case 'delete':
        const delIndex = page.objects.findIndex(o => o.id === delta.objectId);
        if (delIndex !== -1) {
          page.objects.splice(delIndex, 1);
        }
        break;
    }
  };

  const applyDeltaReverse = (delta: EditDelta) => {
    const page = pages.value.find(p => p.pageNumber === delta.pageNumber);
    if (!page) return;

    switch (delta.type) {
      case 'add':
        // Reverse of add is delete
        const addIndex = page.objects.findIndex(o => o.id === delta.objectId);
        if (addIndex !== -1) {
          page.objects.splice(addIndex, 1);
        }
        break;
      case 'modify':
        // Reverse is to restore previous state
        if (delta.previousState) {
          const modIndex = page.objects.findIndex(o => o.id === delta.objectId);
          if (modIndex !== -1) {
            page.objects[modIndex] = delta.previousState;
          }
        }
        break;
      case 'delete':
        // Reverse of delete is add
        if (delta.previousState) {
          page.objects.push(delta.previousState);
        }
        break;
    }
  };

  // Save operations
  const saveEdits = async () => {
    if (!documentId.value || pendingEdits.value.length === 0) return;

    saving.value = true;
    try {
      await api.post(`/documents/${documentId.value}/editor/edits`, {
        edits: pendingEdits.value,
      });
      pendingEdits.value = [];
      lastSaveTime.value = new Date();
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message || 'Failed to save edits';
      error.value = message;
      throw new Error(message);
    } finally {
      saving.value = false;
    }
  };

  // Page operations
  const rotatePage = async (pageNumber: number, degrees: 90 | 180 | 270) => {
    if (!documentId.value) return;

    try {
      await api.post(`/documents/${documentId.value}/rotate-pages`, {
        rotations: [{ pageNumber, degrees }],
      });

      // Update local state
      const page = pages.value.find(p => p.pageNumber === pageNumber);
      if (page) {
        page.rotation = (page.rotation + degrees) % 360;
      }
    } catch (err) {
      error.value = 'Failed to rotate page';
      throw err;
    }
  };

  // Extract text from PDF for editing
  const extractedText = ref<{ pageNumber: number; text: string }[]>([]);
  const extractingText = ref(false);

  const extractPdfText = async () => {
    if (!documentId.value) return;

    extractingText.value = true;
    try {
      const response = await api.post(`/documents/${documentId.value}/extract-text`);
      extractedText.value = response.data.data.pages || [];
      return extractedText.value;
    } catch (err) {
      error.value = 'Failed to extract text from PDF';
      throw err;
    } finally {
      extractingText.value = false;
    }
  };

  const deletePage = async (pageNumber: number) => {
    if (!documentId.value || totalPages.value <= 1) return;

    try {
      await api.post(`/documents/${documentId.value}/delete-pages`, {
        pages: [pageNumber],
      });

      // Update local state
      pages.value = pages.value.filter(p => p.pageNumber !== pageNumber);
      // Renumber remaining pages
      pages.value.forEach((p, i) => {
        p.pageNumber = i + 1;
      });

      if (currentPageIndex.value >= totalPages.value) {
        currentPageIndex.value = totalPages.value - 1;
      }
    } catch (err) {
      error.value = 'Failed to delete page';
      throw err;
    }
  };

  const duplicatePage = async (pageNumber: number) => {
    if (!documentId.value) return;

    try {
      await api.post(`/documents/${documentId.value}/duplicate-pages`, {
        pages: [{ pageNumber, insertAfter: pageNumber }],
      });

      // Reload document to get updated pages
      // For now, just notify that reload is needed
      return true;
    } catch (err) {
      error.value = 'Failed to duplicate page';
      throw err;
    }
  };

  // Apply text edits to PDF (for extracted/edited text)
  // Returns a promise that resolves with edit count, and requires PDF reload callback
  const applyTextEdits = async (reloadPdfCallback?: () => Promise<void>) => {
    if (!documentId.value) return { success: false, count: 0 };

    // Collect all extracted text objects that have been modified
    const textEdits: {
      pageNumber: number;
      originalText: string;
      newText: string;
      x: number;
      pdfBaseline: number; // ORIGINAL PDF baseline (bottom-origin) - sent directly
      width: number;
      height: number;
      fontSize: number;
      fontFamily: string;
      fontBold: boolean;
      fontItalic: boolean;
      color: string;
      pageWidth?: number;
      pageHeight?: number;
    }[] = [];

    // Also collect IDs of extracted text objects to remove after applying
    const extractedTextIds: string[] = [];

    for (const page of pages.value) {
      for (const obj of page.objects) {
        if (
          obj.type === 'text' &&
          obj.data.isExtracted === true &&
          obj.data.originalText &&
          obj.data.text !== obj.data.originalText
        ) {
          // Use original extracted coordinates
          const x = (obj.data.originalX as number) ?? obj.x;
          const width = (obj.data.originalWidth as number) ?? obj.width;
          const height = (obj.data.originalHeight as number) ?? obj.height;
          // CRITICAL: Use the original PDF baseline directly (no conversion!)
          const pdfBaseline = (obj.data.pdfBaseline as number) ?? 0;

          textEdits.push({
            pageNumber: page.pageNumber,
            originalText: obj.data.originalText as string,
            newText: obj.data.text as string,
            x,
            pdfBaseline, // Send original baseline directly
            width,
            height,
            fontSize: (obj.data.fontSize as number) || 12,
            fontFamily: (obj.data.fontFamily as string) || 'Helvetica',
            fontBold: (obj.data.fontBold as boolean) || false,
            fontItalic: (obj.data.fontItalic as boolean) || false,
            color: (obj.data.color as string) || '#000000',
            pageWidth: (obj.data.pageWidth as number) || undefined,
            pageHeight: (obj.data.pageHeight as number) || undefined,
          });

          extractedTextIds.push(obj.id);
        }
      }
    }

    if (textEdits.length === 0) {
      return { success: true, count: 0 };
    }

    // Debug: Log what we're sending to the backend
    console.log('========== APPLYING TEXT EDITS ==========');
    console.log('Number of edits:', textEdits.length);
    textEdits.forEach((edit, i) => {
      console.log(`Edit ${i + 1}:`, {
        original: edit.originalText,
        new: edit.newText,
        x: edit.x,
        pdfBaseline: edit.pdfBaseline,
        fontSize: edit.fontSize,
        pageSize: { w: edit.pageWidth, h: edit.pageHeight },
      });
    });
    console.log('==========================================');

    saving.value = true;
    try {
      await api.post(`/documents/${documentId.value}/edit-text-items`, {
        edits: textEdits,
      });

      // Remove the extracted text overlay objects since they're now baked into the PDF
      for (const page of pages.value) {
        page.objects = page.objects.filter(obj => !extractedTextIds.includes(obj.id));
      }

      // Clear selection
      selectedObjectIds.value = [];

      // Reload the PDF to show the changes
      // This is critical - otherwise the user sees the old PDF with overlays hidden
      if (reloadPdfCallback) {
        await reloadPdfCallback();
      }

      return { success: true, count: textEdits.length, needsReload: !reloadPdfCallback };
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message || 'Failed to apply text edits';
      error.value = message;
      throw new Error(message);
    } finally {
      saving.value = false;
    }
  };

  // Remove images from PDF by covering with white rectangles
  const removeImagesFromPdf = async (
    imageRegions: { pageNumber: number; x: number; y: number; width: number; height: number }[],
    reloadPdfCallback?: () => Promise<void>
  ) => {
    if (!documentId.value || imageRegions.length === 0) {
      return { success: false, count: 0 };
    }

    saving.value = true;
    try {
      await api.post(`/documents/${documentId.value}/remove-images`, {
        imageRegions,
      });

      // Reload the PDF to show the changes
      if (reloadPdfCallback) {
        await reloadPdfCallback();
      }

      return { success: true, count: imageRegions.length };
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message || 'Failed to remove images';
      error.value = message;
      throw new Error(message);
    } finally {
      saving.value = false;
    }
  };

  // Apply added text objects to PDF (embed into document)
  const applyTextToPdf = async (reloadPdfCallback?: () => Promise<void>) => {
    if (!documentId.value) return { success: false, count: 0 };

    // Collect all regular text objects (not extracted/edited ones)
    const textObjects: {
      pageNumber: number;
      x: number;
      y: number;
      text: string;
      fontSize?: number;
      fontFamily?: string;
      fontBold?: boolean;
      fontItalic?: boolean;
      color?: string;
    }[] = [];

    const textObjectIds: string[] = [];

    for (const page of pages.value) {
      for (const obj of page.objects) {
        // Only include regular text objects (not extracted/edited ones)
        if (obj.type === 'text' && !obj.data.isExtracted && obj.data.text) {
          textObjects.push({
            pageNumber: page.pageNumber,
            x: obj.x,
            y: obj.y,
            text: obj.data.text as string,
            fontSize: (obj.data.fontSize as number) || 16,
            fontFamily: (obj.data.fontFamily as string) || 'Helvetica',
            fontBold: (obj.data.fontBold as boolean) || false,
            fontItalic: (obj.data.fontItalic as boolean) || false,
            color: (obj.data.color as string) || '#000000',
          });
          textObjectIds.push(obj.id);
        }
      }
    }

    if (textObjects.length === 0) {
      return { success: true, count: 0 };
    }

    saving.value = true;
    try {
      await api.post(`/documents/${documentId.value}/add-text`, {
        textObjects,
      });

      // Remove the text objects from overlay since they're now in the PDF
      for (const page of pages.value) {
        page.objects = page.objects.filter(obj => !textObjectIds.includes(obj.id));
      }

      // Clear selection and pending edits for these objects
      selectedObjectIds.value = [];
      pendingEdits.value = pendingEdits.value.filter(
        edit => !textObjectIds.includes(edit.objectId)
      );

      // Reload the PDF to show the embedded text
      if (reloadPdfCallback) {
        await reloadPdfCallback();
      }

      return { success: true, count: textObjects.length };
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message || 'Failed to add text to PDF';
      error.value = message;
      throw new Error(message);
    } finally {
      saving.value = false;
    }
  };

  // Apply added image objects to PDF (embed into document)
  const applyImagesToPdf = async (reloadPdfCallback?: () => Promise<void>) => {
    if (!documentId.value) return { success: false, count: 0 };

    const imageObjects: {
      pageNumber: number;
      x: number;
      y: number;
      width: number;
      height: number;
      dataUrl: string;
    }[] = [];

    const imageObjectIds: string[] = [];

    for (const page of pages.value) {
      for (const obj of page.objects) {
        if ((obj.type === 'image' || obj.type === 'signature') && obj.data.dataUrl && !obj.data.placeholder) {
          // Sent in displayed space (top-left origin, PDF points). The backend
          // maps it to the page's real coordinates, so rotated pages, CropBoxes
          // and non-Letter page sizes all land where the user placed the image.
          imageObjects.push({
            pageNumber: page.pageNumber,
            x: obj.x,
            y: obj.y,
            width: obj.width,
            height: obj.height,
            dataUrl: obj.data.dataUrl as string,
          });
          imageObjectIds.push(obj.id);
        }
      }
    }

    if (imageObjects.length === 0) {
      return { success: true, count: 0 };
    }

    saving.value = true;
    try {
      await api.post(`/documents/${documentId.value}/add-images`, {
        images: imageObjects,
      });

      // Remove image objects from overlay since they're now in the PDF
      for (const page of pages.value) {
        page.objects = page.objects.filter(obj => !imageObjectIds.includes(obj.id));
      }

      selectedObjectIds.value = [];
      pendingEdits.value = pendingEdits.value.filter(
        edit => !imageObjectIds.includes(edit.objectId)
      );

      if (reloadPdfCallback) {
        await reloadPdfCallback();
      }

      return { success: true, count: imageObjects.length };
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message || 'Failed to add images to PDF';
      error.value = message;
      throw new Error(message);
    } finally {
      saving.value = false;
    }
  };

  // Check if there are pending image objects to embed
  const hasPendingImageObjects = computed(() => {
    for (const page of pages.value) {
      for (const obj of page.objects) {
        if ((obj.type === 'image' || obj.type === 'signature') && obj.data.dataUrl && !obj.data.placeholder) {
          return true;
        }
      }
    }
    return false;
  });

  // Check if there are pending text objects to embed
  const hasPendingTextObjects = computed(() => {
    for (const page of pages.value) {
      for (const obj of page.objects) {
        if (obj.type === 'text' && !obj.data.isExtracted && obj.data.text) {
          return true;
        }
      }
    }
    return false;
  });

  // Check if there are pending text edits (extracted text that was modified)
  const hasPendingTextEdits = computed(() => {
    for (const page of pages.value) {
      for (const obj of page.objects) {
        if (
          obj.type === 'text' &&
          obj.data.isExtracted === true &&
          obj.data.originalText &&
          obj.data.text !== obj.data.originalText
        ) {
          return true;
        }
      }
    }
    return false;
  });

  // Reset state
  const resetEditor = () => {
    documentId.value = null;
    documentInfo.value = null;
    pdfBytes.value = null;
    pages.value = [];
    currentPageIndex.value = 0;
    zoom.value = 100;
    activeTool.value = 'select';
    selectedObjectIds.value = [];
    clipboard.value = [];
    editHistory.value = [];
    historyIndex.value = -1;
    pendingEdits.value = [];
    lastSaveTime.value = null;
    loading.value = false;
    saving.value = false;
    error.value = null;
  };

  return {
    // State
    documentId,
    documentInfo,
    pdfBytes,
    loading,
    saving,
    error,
    pages,
    currentPageIndex,
    totalPages,
    zoom,
    viewMode,
    activeTool,
    toolSettings,
    selectedObjectIds,
    selectedObjects,
    clipboard,
    editHistory,
    historyIndex,
    autoSaveEnabled,
    lastSaveTime,
    isDirty,
    currentPage,
    canUndo,
    canRedo,
    extractedText,
    extractingText,

    // Actions
    loadDocument,
    setPages,
    goToPage,
    setZoom,
    zoomIn,
    zoomOut,
    setActiveTool,
    updateToolSettings,
    addObject,
    updateObject,
    deleteObject,
    deleteSelectedObjects,
    selectObject,
    clearSelection,
    selectAllOnPage,
    copyToClipboard,
    pasteFromClipboard,
    undo,
    redo,
    saveEdits,
    rotatePage,
    deletePage,
    duplicatePage,
    extractPdfText,
    applyTextEdits,
    applyTextToPdf,
    applyImagesToPdf,
    removeImagesFromPdf,
    hasPendingTextEdits,
    hasPendingTextObjects,
    hasPendingImageObjects,
    resetEditor,
  };
});
